/* istanbul ignore file */

import globalWindow from '@shared/core/globals';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEffect } from 'react';

const document = globalWindow.document;

const IFRAME_SCREEN_ID = 'iframe-screen';
const SCREEN_STYLE = 'position: fixed; width: 100%; height: 100%; z-index: 200; cursor: pointer;';
const SCREEN_MUTATION_INIT: MutationObserverInit = { attributes: true, attributeFilter: ['id', 'style'] };
const BODY_MUTATION_INIT: MutationObserverInit = { childList: true };

export type UseIframeScreenArgs = {
  onClick: (ev: MouseEvent) => void;
};

export const useIframeScreen = ({ onClick }: UseIframeScreenArgs) => {
  const isPreviewing = isInIframe();

  useEffect(() => {
    if (isPreviewing && document) {
      const _insertScreen = (): HTMLDivElement => {
        const screen = document.createElement('div');
        screen.id = IFRAME_SCREEN_ID;
        screen.style.cssText = SCREEN_STYLE;
        screen.onclick = onClick;

        const body = document.body;
        body.prepend(screen);
        return screen;
      };

      ////////////////////////////
      // Mutation observers

      let screenRef: HTMLDivElement = _insertScreen();
      const screenObserver = new MutationObserver((mutations, observer) => {
        mutations.forEach(mutation => {
          if (screenRef.style.cssText !== SCREEN_STYLE) {
            // Disconnect observer before mutating screen ref
            observer.disconnect();
            // Mutate screen ref styles
            screenRef.id = IFRAME_SCREEN_ID;
            screenRef.style.cssText = SCREEN_STYLE;
            // Restart observer
            observer.observe(screenRef, SCREEN_MUTATION_INIT);
          }
        });
      });
      const bodyObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.removedNodes.length) {
            // Since we're not observing attribute changes for the entire subtree, we want to verify:
            // 1. removed node id matches the screen id
            // 2. check if removed node is the current local screen ref
            const removedScreen = Array.prototype.slice.call<NodeList, [], HTMLElement[]>(mutation.removedNodes).find(node => node.id === IFRAME_SCREEN_ID || node === screenRef);

            // Screen has been removed - add it again
            if (removedScreen) {
              // Disconnect observer
              screenObserver.disconnect();
              // Update local screen reference
              screenRef = _insertScreen();

              // Restart obsever
              screenObserver.observe(screenRef, SCREEN_MUTATION_INIT);
            }
          }
        });
      });

      ////////////////////////////
      // Events

      const _eventClickHandler = (ev: MouseEvent) => {
        if ((ev.target as HTMLElement).id !== IFRAME_SCREEN_ID) {
          ev.preventDefault();
          ev.stopPropagation();
        }
      };

      const _eventKeydownHandler = (ev: KeyboardEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
        return false;
      };

      ////////////////////////////
      // Initiate observers + event handlers
      screenObserver.observe(screenRef, SCREEN_MUTATION_INIT);
      bodyObserver.observe(document.body, BODY_MUTATION_INIT);
      document.addEventListener('click', _eventClickHandler);
      document.addEventListener('keydown', _eventKeydownHandler);

      return () => {
        screenObserver.disconnect();
        bodyObserver.disconnect();
        document.removeEventListener('click', _eventClickHandler);
        document.removeEventListener('keydown', _eventKeydownHandler);
      };
    }
    return () => {};
  }, [isPreviewing, onClick]);
};
